import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post, put } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError, useAuth } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { DataTable, Dialog, PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, Select, SkeletonText } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { WardsAndBedsTab } from './WardsAndBedsTab';
import { COUNTRY_BASICS, type CountryCode, type ModuleCode } from '@his/shared';
import { moduleName, orgReturnTo, orgTabOf, type Branch, type Department, type Specialty, type Store } from './org-types';

function ApproveBranchButton({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const approve = useMutation({
    mutationFn: () => put(`/org/branches/${branch.id}/approve`, {}),
    onSuccess: () => {
      toast.success(tr('admin', 'branch_approved'), tr('admin', 'x_is_open_for_sign_in', { name: branch.name }));
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_approve_the_branch'), describeError(error)),
  });
  return (
    <Button size="sm" onClick={() => approve.mutate()} loading={approve.isPending}>
      {tr('admin', 'approve_branch')}
    </Button>
  );
}

function ResubmitBranchButton({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const resubmit = useMutation({
    mutationFn: () => post(`/org/branches/${branch.id}/resubmit`),
    onSuccess: () => {
      toast.success(tr('admin', 'branch_sent_again'));
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_send_branch_again'), describeError(error)),
  });
  return (
    <Button size="sm" variant="secondary" onClick={() => resubmit.mutate()} loading={resubmit.isPending}>
      {tr('admin', 'send_branch_again')}
    </Button>
  );
}

function BranchApprovalChip({ branch }: { branch: Branch }) {
  if (branch.approvalStatus === 'pending') {
    return (
      <Badge variant="warning" className="ms-2">
        {tr('admin', 'pending_owner_approval')}
      </Badge>
    );
  }
  if (branch.approvalStatus === 'rejected') {
    return (
      <Badge variant="destructive" className="ms-2">
        {tr('admin', 'branch_status_rejected')}
      </Badge>
    );
  }
  if (branch.approvedAt) {
    return (
      <Badge variant="success" className="ms-2">
        {tr('admin', 'branch_status_approved')}
      </Badge>
    );
  }
  return null;
}

function inTreeOrder(branches: readonly Branch[]): Branch[] {
  const top = branches.filter((branch) => !branch.parentBranchId || !branches.some((parent) => parent.id === branch.parentBranchId));
  return top.flatMap((parent) => [parent, ...branches.filter((child) => child.parentBranchId === parent.id)]);
}

/** Branches, departments and units — the organisation tree (SRS 3.1). */
export function BranchesPage() {
  const { user, can, canWrite, hasModule, isSuperAdmin } = useAuth();
  const ownerApproves = user?.impersonation?.kind === 'owner' && user.deploymentMode === 'CLOUD';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = orgTabOf(params.get('tab'));
  const setTab = (id: string) => setParams(id === 'branches' ? {} : { tab: id }, { replace: true });
  const openForm = (path: string) => navigate(`${path}?returnTo=${encodeURIComponent(orgReturnTo(tab))}`);
  // Stores hold stock; a place without pharmacy or inventory has none to show.
  const stockKept = hasModule('pharmacy') || hasModule('inventory');
  const doctorsKept = (['opd', 'ipd', 'er', 'ot', 'radiology', 'maternity', 'dialysis', 'daycare', 'dental'] as ModuleCode[]).some((module) =>
    hasModule(module),
  );
  const manageDepartments = can('department.manage') && canWrite;
  const bedsKept = hasModule('ipd') && can('bed.read');
  const [addingStandard, setAddingStandard] = useState(false);
  const [addingStandardSpecialties, setAddingStandardSpecialties] = useState(false);
  const [branchAdminOfferDismissed, setBranchAdminOfferDismissed] = useState(false);

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => get<Branch[]>('/org/branches?all=1'),
  });
  const branchRows = branches.data ? inTreeOrder(branches.data) : undefined;
  const branchById = (id: string | null | undefined) => branches.data?.find((branch) => branch.id === id);
  const waitingForOwner = isSuperAdmin && (branches.data?.some((branch) => branch.approvalStatus === 'pending') ?? false);
  const resendAllowed = isSuperAdmin && can('branch.manage') && canWrite;

  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => get<Department[]>('/org/departments'),
    enabled: tab === 'departments',
  });

  const specialties = useQuery({
    queryKey: ['specialties'],
    queryFn: () => get<Specialty[]>('/org/specialties'),
    enabled: tab === 'specialties',
  });

  const offerBranchAdmin = isSuperAdmin && canWrite && (branches.data?.length ?? 0) >= 2 && !branchAdminOfferDismissed;
  const roleCodes = useQuery({
    queryKey: ['roles'],
    queryFn: () => get<{ code: string }[]>('/org/roles'),
    enabled: offerBranchAdmin,
  });
  const showBranchAdminOffer = offerBranchAdmin && Boolean(roleCodes.data) && !roleCodes.data?.some((role) => role.code === 'branch_admin');

  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: () => get<Store[]>('/pharmacy/stores'),
    enabled: tab === 'stores',
  });

  return (
    <>
      <PageHeader
        title={tr('admin', 'branches')}
        description={tr('admin', 'sites_departments_and_units')}
        actions={
          tab === 'wards' ? (
            can('bed.manage') && canWrite && <Button onClick={() => openForm('/admin/wards/new')}>{tr('admin', 'add_ward')}</Button>
          ) : tab === 'stores' ? (
            can('store.manage') && <Button onClick={() => openForm('/admin/stores/new')}>{tr('admin', 'add_store')}</Button>
          ) : tab === 'departments' ? (
            manageDepartments && (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setAddingStandard(true)}>
                  {tr('admin', 'add_standard_departments')}
                </Button>
                <Button onClick={() => openForm('/admin/departments/new')}>{tr('admin', 'add_department')}</Button>
              </div>
            )
          ) : tab === 'specialties' ? (
            manageDepartments && (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setAddingStandardSpecialties(true)}>
                  {tr('admin', 'add_standard_specialties')}
                </Button>
                <Button onClick={() => openForm('/admin/specialties/new')}>{tr('admin', 'add_specialty')}</Button>
              </div>
            )
          ) : (
            can('branch.manage') && canWrite && <Button onClick={() => openForm('/admin/branches/new')}>{tr('admin', 'add_branch')}</Button>
          )
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'branches', label: tr('admin', 'branches') },
          { id: 'departments', label: tr('admin', 'departments') },
          ...(doctorsKept ? [{ id: 'specialties', label: tr('admin', 'specialties') }] : []),
          ...(bedsKept ? [{ id: 'wards', label: tr('admin', 'wards_and_beds') }] : []),
          ...(stockKept ? [{ id: 'stores', label: tr('admin', 'stores') }] : []),
        ]}
      />

      {tab === 'branches' && showBranchAdminOffer && (
        <BranchAdminOffer onDismiss={() => setBranchAdminOfferDismissed(true)} />
      )}

      {tab === 'branches' && waitingForOwner && (
        <Alert variant="warning" className="mb-4" title={tr('admin', 'branch_waiting_for_owner_title')}>
          {tr('admin', 'branch_waiting_for_owner_body')}
        </Alert>
      )}

      {tab === 'branches' && (
        <DataTable
          rows={branchRows}
          loading={branches.isLoading} refreshing={branches.isFetching}
          error={branches.error}
          rowKey={(row) => row.id}
          columns={[
            {
              header: tr('admin', 'code'),
              width: '110px',
              cell: (row) => (
                <span className={`font-mono text-xs ${row.parentBranchId ? 'ps-4' : ''}`}>{row.code}</span>
              ),
            },
            {
              header: tr('admin', 'name'),
              cell: (row) => (
                <div>
                  <p>
                    {row.name}
                    {isSuperAdmin && <BranchApprovalChip branch={row} />}
                  </p>
                  {isSuperAdmin && row.approvalStatus === 'rejected' && (
                    <div className="mt-1 text-xs">
                      {row.rejectionReason && <p className="text-destructive">{tr('admin', 'branch_rejected_reason', { reason: row.rejectionReason })}</p>}
                      <p className="text-muted-foreground">{tr('admin', 'branch_rejected_next_step')}</p>
                    </div>
                  )}
                  {row.parentBranchId && (
                    <p className="text-xs text-muted-foreground">{tr('admin', 'sub_branch_of')} {branchById(row.parentBranchId)?.name ?? '—'}</p>
                  )}
                  {(row.categoryModules?.length ?? 0) > 0 && (
                    <p className="text-xs text-muted-foreground">{row.categoryModules?.map(moduleName).join(' · ')}</p>
                  )}
                </div>
              ),
            },
            { header: tr('admin', 'city'), cell: (row) => row.city ?? '—' },
            {
              header: tr('admin', 'country'),
              cell: (row) =>
                row.country ? (
                  <span>
                    {COUNTRY_BASICS[row.country as CountryCode]?.name ?? row.country}
                    <span className="ms-1 text-xs text-muted-foreground">{row.currency}</span>
                  </span>
                ) : (
                  <Badge variant="warning">{tr('admin', 'not_set')}</Badge>
                ),
            },
            { header: tr('admin', 'phone'), cell: (row) => row.phone ?? '—' },
            { header: tr('admin', 'departments'), align: 'right', cell: (row) => row._count.departments },
            { header: tr('admin', 'staff'), align: 'right', cell: (row) => row._count.users },
            {
              header: tr('admin', 'status'),
              cell: (row) => <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? tr('admin', 'active') : tr('admin', 'inactive')}</Badge>,
            },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <div className="flex justify-end gap-2">
                  {ownerApproves && row.approvalStatus !== 'approved' && <ApproveBranchButton branch={row} />}
                  {resendAllowed && row.approvalStatus === 'rejected' && <ResubmitBranchButton branch={row} />}
                  {can('branch.manage') && canWrite && (
                    <Button size="sm" variant="outline" onClick={() => openForm(`/admin/branches/${row.id}/edit`)}>
                      {tr('admin', 'edit')}
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      {tab === 'departments' && (
        <>
          {hasModule('lab') && (
            <p className="mb-3 text-xs text-muted-foreground">{tr('admin', 'laboratory_test_sections_are_set_in_the_test')}</p>
          )}
          <DataTable
            rows={departments.data}
            loading={departments.isLoading} refreshing={departments.isFetching}
            error={departments.error}
            rowKey={(row) => row.id}
            empty={tr('admin', 'no_departments_yet_add_the_standard_ones_to')}
            columns={[
              { header: tr('admin', 'code'), width: '100px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              { header: tr('admin', 'department'), sort: (row) => row.name, cell: (row) => row.name },
              { header: tr('admin', 'kind'), sort: (row) => row.kind, cell: (row) => <Badge variant="secondary">{humanize(row.kind)}</Badge> },
              {
                header: tr('admin', 'branch'),
                sort: (row) => branchById(row.branchId)?.name,
                cell: (row) => branchById(row.branchId)?.name ?? '—',
              },
              {
                header: tr('admin', 'status'),
                sort: (row) => row.isActive,
                cell: (row) => <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? tr('admin', 'active') : tr('admin', 'inactive')}</Badge>,
              },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  manageDepartments ? (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openForm(`/admin/departments/${row.id}/edit`)}>
                        {tr('admin', 'edit')}
                      </Button>
                      <DepartmentActiveButton department={row} />
                    </div>
                  ) : null,
              },
            ]}
          />
        </>
      )}

      {tab === 'specialties' && (
        <DataTable
          rows={specialties.data}
          loading={specialties.isLoading} refreshing={specialties.isFetching}
          error={specialties.error}
          rowKey={(row) => row.id}
          empty={tr('admin', 'no_specialties_yet_add_the_standard_ones_to')}
          columns={[
            { header: tr('admin', 'code'), width: '110px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
            { header: tr('admin', 'specialty'), sort: (row) => row.name, cell: (row) => row.name },
            ...(hasModule('nphies')
              ? [{ header: tr('admin', 'nphies_practice_code'), sort: (row: Specialty) => row.nphiesPracticeCode, cell: (row: Specialty) => row.nphiesPracticeCode ?? '—' }]
              : []),
            {
              header: tr('admin', 'status'),
              sort: (row) => row.isActive,
              cell: (row) => <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? tr('admin', 'active') : tr('admin', 'inactive')}</Badge>,
            },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                manageDepartments ? (
                  <Button size="sm" variant="outline" onClick={() => openForm(`/admin/specialties/${row.id}/edit`)}>
                    {tr('admin', 'edit')}
                  </Button>
                ) : null,
            },
          ]}
        />
      )}

      {tab === 'stores' && (
        <>
          <Alert className="mb-4">
            {tr('admin', 'a_store_is_a_stock_location_the_main_pharmacy')}
          </Alert>
          <DataTable
            rows={stores.data}
            loading={stores.isLoading} refreshing={stores.isFetching}
            error={stores.error}
            rowKey={(row) => row.id}
            empty={tr('admin', 'no_stores_yet')}
            columns={[
              { header: tr('admin', 'code'), width: '100px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              { header: tr('admin', 'store'), sort: (row) => row.name, cell: (row) => row.name },
              { header: tr('admin', 'type'), sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type)}</Badge> },
              {
                header: tr('admin', 'branch'),
                sort: (row) => branchById(row.branchId)?.name,
                cell: (row) => branchById(row.branchId)?.name ?? '—',
              },
              { header: tr('admin', 'ward'), sort: (row) => row.ward?.name, cell: (row) => row.ward?.name ?? '—' },
              {
                header: tr('admin', 'sells'),
                sort: (row) => row.isSellingPoint,
                cell: (row) => (row.isSellingPoint ? 'Yes' : 'No'),
              },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  can('store.manage') ? (
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => openForm(`/admin/stores/${row.id}/edit`)}>
                        {tr('admin', 'edit')}
                      </Button>
                      <CloseStoreButton store={row} />
                    </div>
                  ) : null,
              },
            ]}
          />
        </>
      )}

      {tab === 'wards' && <WardsAndBedsTab branches={branches.data ?? []} />}

      {addingStandard && (
        <StandardDepartmentsDialog branches={branches.data ?? []} onClose={() => setAddingStandard(false)} />
      )}
      {addingStandardSpecialties && <StandardSpecialtiesDialog onClose={() => setAddingStandardSpecialties(false)} />}
    </>
  );
}

function BranchAdminOffer({ onDismiss }: { onDismiss: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const add = useMutation({
    mutationFn: () => post('/org/roles/standard/branch_admin'),
    onSuccess: () => {
      toast.success(tr('admin', 'branch_admin_role_added'), tr('admin', 'give_it_to_someone_under_staff_and_roles'));
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-groups'] });
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_add_the_role'), describeError(caught)),
  });
  return (
    <Alert className="mb-4" title={tr('admin', 'running_more_than_one_branch')}>
      <p>{tr('admin', 'the_branch_admin_role_lets_someone_run_one')}</p>
      <div className="mt-2 flex gap-2">
        <Button size="sm" loading={add.isPending} onClick={() => add.mutate()}>
          {tr('admin', 'add_the_branch_admin_role')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          {tr('admin', 'not_now')}
        </Button>
      </div>
    </Alert>
  );
}

function DepartmentActiveButton({ department }: { department: Department }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      put(`/org/departments/${department.id}`, {
        branchId: department.branchId,
        code: department.code,
        name: department.name,
        kind: department.kind,
        isActive: !department.isActive,
      }),
    onSuccess: () => {
      toast.success(department.isActive ? tr('admin', 'x_switched_off', { name: department.name }) : tr('admin', 'x_switched_on', { name: department.name }));
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_change_the_department'), describeError(caught)),
  });
  return (
    <Button size="sm" variant={department.isActive ? 'ghost' : 'secondary'} loading={toggle.isPending} onClick={() => toggle.mutate()}>
      {department.isActive ? tr('admin', 'deactivate') : tr('admin', 'reactivate')}
    </Button>
  );
}

function StandardDepartmentsDialog({ branches, onClose }: { branches: Branch[]; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const lock = useBranchLock();
  const [branchId, setBranchId] = useState(lock.locked ? lock.branchId : branches.length === 1 ? branches[0].id : '');
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [copyFrom, setCopyFrom] = useState('');
  const [error, setError] = useState<string | null>(null);

  const offered = useQuery({
    queryKey: ['standard-departments', branchId],
    queryFn: () =>
      get<{ code: string; name: string; kind: string; preselected: boolean }[]>(`/org/departments/standard?branchId=${branchId}`),
    enabled: Boolean(branchId),
  });
  const selected = chosen ?? new Set((offered.data ?? []).filter((department) => department.preselected).map((department) => department.code));

  const done = (added: number) => {
    toast.success(added === 0 ? tr('admin', 'nothing_new_to_add') : tr('admin', 'x_department_s_added', { added: added }));
    void queryClient.invalidateQueries({ queryKey: ['departments'] });
    void queryClient.invalidateQueries({ queryKey: ['standard-departments'] });
    void queryClient.invalidateQueries({ queryKey: ['branches'] });
    onClose();
  };

  const add = useMutation({
    mutationFn: () => post<{ added: number }>('/org/departments/standard', { branchId, codes: [...selected] }),
    onSuccess: (result) => done(result.added),
    onError: (caught) => setError(describeError(caught)),
  });

  const copy = useMutation({
    mutationFn: () => post<{ added: number }>('/org/departments/copy', { fromBranchId: copyFrom, toBranchId: branchId }),
    onSuccess: (result) => done(result.added),
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={tr('admin', 'add_standard_departments')}
      description={tr('admin', 'the_usual_departments_for_the_modules_you_hold')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('admin', 'cancel')}
          </Button>
          <Button
            loading={add.isPending}
            disabled={!branchId || selected.size === 0}
            onClick={() => {
              setError(null);
              add.mutate();
            }}
          >
            {tr('admin', 'add')} {selected.size || ''} {tr('admin', 'department_2')}{selected.size === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {branches.length > 1 && (
          <Field label={tr('admin', 'branch')} required>
            <Select
              value={branchId}
              readOnly={lock.locked}
              onChange={(e) => {
                setBranchId(e.target.value);
                setChosen(null);
                setCopyFrom('');
              }}
            >
              <option value="">{tr('admin', 'select_a_branch')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {offered.isLoading && <SkeletonText lines={2} />}
        {offered.data && offered.data.length === 0 && (
          <p className="text-sm text-muted-foreground">{tr('admin', 'this_branch_already_has_every_standard')}</p>
        )}
        {offered.data && offered.data.length > 0 && (
          <div className="grid gap-1 sm:grid-cols-2">
            {offered.data.map((department) => (
              <Checkbox
                key={department.code}
                checked={selected.has(department.code)}
                onCheckedChange={(checked) => {
                  const next = new Set(selected);
                  if (checked) next.add(department.code);
                  else next.delete(department.code);
                  setChosen(next);
                }}
                label={department.name}
                description={<span className="font-mono text-[11px]">{department.code}</span>}
              />
            ))}
          </div>
        )}
        {branchId && branches.length > 1 && (
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <Field label={tr('admin', 'or_copy_the_departments_of_another_branch')} className="min-w-[14rem] flex-1">
              <Select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                <option value="">{tr('admin', 'choose_a_branch')}</option>
                {branches
                  .filter((branch) => branch.id !== branchId)
                  .map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Button
              variant="secondary"
              loading={copy.isPending}
              disabled={!copyFrom}
              onClick={() => {
                setError(null);
                copy.mutate();
              }}
            >
              {tr('admin', 'copy')}
            </Button>
          </div>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function StandardSpecialtiesDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const offered = useQuery({
    queryKey: ['standard-specialties'],
    queryFn: () => get<{ code: string; name: string }[]>('/org/specialties/standard'),
  });

  const add = useMutation({
    mutationFn: () => post<{ added: number }>('/org/specialties/standard', { codes: [...selected] }),
    onSuccess: (result) => {
      toast.success(`${result.added} specialt${result.added === 1 ? 'y' : 'ies'} added`);
      void queryClient.invalidateQueries({ queryKey: ['specialties'] });
      void queryClient.invalidateQueries({ queryKey: ['standard-specialties'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const all = (offered.data ?? []).length > 0 && selected.size === (offered.data ?? []).length;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={tr('admin', 'add_standard_specialties')}
      description={tr('admin', 'common_specialties_not_yet_on_your_list')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('admin', 'cancel')}
          </Button>
          <Button
            loading={add.isPending}
            disabled={selected.size === 0}
            onClick={() => {
              setError(null);
              add.mutate();
            }}
          >
            {tr('admin', 'add')} {selected.size || ''}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {offered.isLoading && <SkeletonText lines={2} />}
        {offered.data && offered.data.length === 0 && <p className="text-sm text-muted-foreground">{tr('admin', 'every_standard_specialty_is_already_on_your_list')}</p>}
        {offered.data && offered.data.length > 0 && (
          <>
            <button
              type="button"
              className="text-xs text-primary hover:underline focus-ring"
              onClick={() => setSelected(all ? new Set() : new Set(offered.data.map((specialty) => specialty.code)))}
            >
              {all ? tr('admin', 'select_none') : tr('admin', 'select_all')}
            </button>
            <div className="grid gap-1 sm:grid-cols-2">
              {offered.data.map((specialty) => (
                <Checkbox
                  key={specialty.code}
                  checked={selected.has(specialty.code)}
                  onCheckedChange={(checked) => {
                    const next = new Set(selected);
                    if (checked) next.add(specialty.code);
                    else next.delete(specialty.code);
                    setSelected(next);
                  }}
                  label={specialty.name}
                />
              ))}
            </div>
          </>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

/**
 * Closes a store.
 *
 * The server refuses while any batch still has quantity on hand — a closed
 * store holding stock would drop that quantity out of every valuation without
 * anybody having counted it. The error says so, so no confirmation prompt is
 * needed beyond the click.
 */
function CloseStoreButton({ store }: { store: Store }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: () =>
      store.isActive
        ? del(`/pharmacy/stores/${store.id}`)
        : post(`/pharmacy/stores/${store.id}/reopen`),
    onSuccess: () => {
      toast.success(store.isActive ? tr('admin', 'store_closed') : tr('admin', 'store_reopened'));
      void queryClient.invalidateQueries({ queryKey: ['stores'] });
      void queryClient.invalidateQueries({ queryKey: ['pharmacy-stores'] });
    },
    onError: (caught) =>
      toast.error(store.isActive ? tr('admin', 'could_not_close_the_store') : tr('admin', 'could_not_reopen_the_store'), describeError(caught)),
  });

  return (
    <Button
      variant={store.isActive ? 'destructive' : 'secondary'}
      size="sm"
      loading={toggle.isPending}
      onClick={() => toggle.mutate()}
    >
      {store.isActive ? tr('admin', 'close') : tr('admin', 'reopen')}
    </Button>
  );
}
